import React, { useEffect, useState } from "react";
import { History, RefreshCw, Loader2, Download } from "lucide-react";
import { adminApi } from "../lib/api";
import AuditEntryRow from "../components/AuditEntryRow";

const ACTIONS = ["", "create", "update", "delete", "revoke", "change_password", "reset_password"];
const ENTITIES = ["", "agent", "document", "feedback", "subscription", "settings", "account"];

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ actor: "", entity_type: "", action: "" });
  const [skip, setSkip] = useState(0);
  const PAGE = 50;

  const load = (reset = true) => {
    setLoading(true);
    const params = { limit: PAGE, skip: reset ? 0 : skip };
    for (const k of ["actor", "entity_type", "action"]) if (filters[k]) params[k] = filters[k];
    adminApi.auditList(params)
      .then(({ data }) => {
        setItems(reset ? (data.items || []) : [...items, ...(data.items || [])]);
        setTotal(data.total || 0);
        if (reset) setSkip(PAGE); else setSkip((s) => s + PAGE);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(true); }, [filters]); // eslint-disable-line

  const exportCsv = () => {
    const rows = [
      ["created_at", "actor_email", "actor_role", "action", "entity_type", "entity_id", "entity_label", "details"],
      ...items.map((e) => [
        e.created_at, e.actor_email, e.actor_role || "", e.action, e.entity_type,
        e.entity_id || "", (e.entity_label || "").replace(/"/g, '""'), JSON.stringify(e.details || {}).replace(/"/g, '""'),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lexhab-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-audit-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">/ journal d'audit</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight flex items-center gap-3">
            <History size={26} strokeWidth={1.7} /> Journal d'audit
          </h1>
          <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
            Trace horodatée de toutes les actions admin sensibles (utilisateurs, documents, abonnements, paramètres, mots de passe).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => load(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-primary/20 text-primary rounded-sm hover:bg-primary/5 transition-colors"
                  data-testid="audit-reload-btn">
            <RefreshCw size={13} /> Actualiser
          </button>
          <button type="button" onClick={exportCsv}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 bg-primary text-white rounded-sm hover:bg-primary-light transition-colors"
                  data-testid="audit-export-btn">
            <Download size={13} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-primary/15 rounded-sm p-4 mb-6 grid md:grid-cols-4 gap-3" data-testid="audit-filters">
        <Input
          label="Acteur (email)"
          value={filters.actor}
          onChange={(v) => setFilters({ ...filters, actor: v })}
          placeholder="admin@votre-domaine.com"
          testid="audit-filter-actor"
        />
        <Select
          label="Type d'entité"
          value={filters.entity_type}
          onChange={(v) => setFilters({ ...filters, entity_type: v })}
          options={ENTITIES}
          testid="audit-filter-entity"
        />
        <Select
          label="Action"
          value={filters.action}
          onChange={(v) => setFilters({ ...filters, action: v })}
          options={ACTIONS}
          testid="audit-filter-action"
        />
        <div className="flex items-end">
          <div className="text-xs text-foreground/60 font-mono">
            {items.length} / {total} événement{total > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-primary/15 rounded-sm overflow-hidden" data-testid="audit-list">
        {loading && items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-foreground/50">
            <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-foreground/50">Aucun événement.</div>
        ) : (
          <ul>
            {items.map((e) => <AuditEntryRow key={e.id} entry={e} />)}
          </ul>
        )}
      </div>

      {items.length < total && (
        <div className="mt-6 text-center">
          <button type="button" onClick={() => load(false)} disabled={loading}
                  className="inline-flex items-center gap-2 border border-primary/20 text-primary px-5 py-2 rounded-sm text-sm font-medium hover:bg-primary/5 transition-colors disabled:opacity-60"
                  data-testid="audit-load-more">
            {loading ? <Loader2 size={13} className="animate-spin" /> : null}
            Charger plus
          </button>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">{label}</span>
      <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary bg-white">
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder} className="w-full bg-transparent text-sm py-2 outline-none font-mono" />
      </div>
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">{label}</span>
      <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary bg-white">
        <select value={value} onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent text-sm py-2 outline-none">
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt || "— tous —"}</option>
          ))}
        </select>
      </div>
    </label>
  );
}
